import {
  IconBuilding,
  IconBuildingPlus,
  IconBuildingSkyscraper,
  // IconCalendarClock,  // restore with the Calendar nav item below
  IconCalendarEvent,
  IconCash,
  IconChecklist,
  IconClipboardCheck,
  IconClipboardText,
  IconFileCheck,
  IconLayoutGrid,
  IconMicrophone,
  IconReportAnalytics,
  IconRocket,
  IconScale,
  IconSchool,
  IconShieldCheck,
  IconUsers,
} from '@tabler/icons-react';

/** Grouped sidebar navigation (super-admin layout). */
export const NAV_SECTIONS = [
  {
    title: 'Dashboard',
    items: [{ key: 'overview', label: 'Overview', path: '/dashboard', icon: IconLayoutGrid }],
  },
  {
    title: 'User & Access',
    items: [
      {
        key: 'roles',
        label: 'Roles & Users',
        path: '/dashboard/roles',
        icon: IconShieldCheck,
        subtitle: 'Manage who has access and what they can do',
      },
    ],
  },
  {
    title: 'Centers',
    items: [
      { key: 'centers', label: 'Centers', path: '/dashboard/centers', icon: IconBuildingSkyscraper, subtitle: 'Every Anablr center on the platform and how it is performing.' },
      { key: 'startups', label: 'Startups', path: '/dashboard/startups', icon: IconRocket, subtitle: 'All startups onboarded across every center.' },
    ],
  },
  {
    title: 'Programs',
    items: [
      {
        key: 'programs',
        label: 'Programs',
        path: '/dashboard/programs',
        icon: IconClipboardText,
        subtitle: 'Every program your centre is running — drafts, open windows, evaluated, closed.',
        // "Manage Collaborators" on a program's own page deep-links here; keep
        // the sidebar on "Programs" instead of jumping to "Collaborations".
        match: ['/dashboard/collaborations/manage/applications'],
      },
      {
        key: 'evaluation-processes',
        label: 'Evaluation Processes',
        path: '/dashboard/evaluation-processes',
        icon: IconClipboardCheck,
        subtitle: 'Reusable evaluation workflows you can assign to any program.',
      },
      {
        key: 'evaluation-matrix',
        label: 'Evaluation Matrix',
        path: '/dashboard/evaluation-matrix',
        icon: IconScale,
        subtitle: 'Reusable scoring templates the jury evaluates applications against',
      },
    ],
  },
  {
    title: 'People',
    items: [
      {
        key: 'stakeholders',
        label: 'Stakeholders',
        path: '/dashboard/stakeholders',
        icon: IconUsers,
        subtitle: 'Mentors, jury members, and speakers in one view',
      },
      {
        key: 'speakers',
        label: 'Speakers',
        path: '/dashboard/speakers',
        icon: IconMicrophone,
        subtitle: 'The speaker directory for your events and sessions',
      },
    ],
  },
  {
    title: 'Grants',
    items: [
      {
        key: 'funds',
        label: 'Fund Catalog',
        pageTitle: 'Funds & schemes',
        path: '/dashboard/funds',
        icon: IconCash,
        subtitle: "Every fund your centre manages — sanctioned, disbursed, and what's left to put to work.",
      },
    ],
  },
  {
    title: 'Events',
    items: [
      {
        key: 'events',
        label: 'Events',
        path: '/dashboard/events',
        icon: IconCalendarEvent,
        subtitle: 'Workshops, demo days, and networking sessions across your center',
        // "Manage Collaborators" on an event's own page deep-links here; keep
        // the sidebar on "Events" instead of jumping to "Collaborations".
        match: ['/dashboard/collaborations/manage/events'],
      },
      // UNDER CONSTRUCTION — withdrawn 2026-09-11 at the owner's request.
      // Commented out rather than deleted so it can be restored as one
      // block when the screen is finished. Everything that serves only it
      // is commented with it: its route and lazy import (routes/AppRoutes),
      // and its exclusion from the master layout below.
      // {
      //   // Deliberately alongside Events rather than under it: this screen is
      //   // the two feeds TOGETHER (events + mentoring sessions), center by
      //   // center, which is a different question from either module's own list.
      //   key: 'calendar',
      //   label: 'Calendar',
      //   path: '/dashboard/calendar',
      //   icon: IconCalendarClock,
      //   subtitle: 'Events and mentoring sessions on one calendar, center by center',
      // },
    ],
  },
  {
    title: 'Reports',
    items: [{ key: 'reports', label: 'Reports', path: '/dashboard/reports', icon: IconReportAnalytics, subtitle: 'Analytics across applications, funding, mentoring and jury' }],
  },
];

/**
 * Super-admin modules the master does NOT inherit, by item `key`.
 *
 * The master's panel is otherwise the super admin's (see below), so an item
 * added to a shared section shows up for the master automatically — which is
 * how the common calendar first appeared there. Anything genuinely scoped to
 * running an organization's day-to-day, rather than provisioning tenants,
 * belongs here.
 *
 * Note this is the exclusion list for items inside SHARED sections. A whole
 * section that master shouldn't have (e.g. Workspace/To-dos) is instead
 * spliced into NAV_SECTIONS *below* this file's NAV_SECTIONS_MASTER
 * definition, so master never copies it in the first place.
 */
export const MASTER_EXCLUDED_KEYS = new Set([
  // Calendar is withdrawn (see above):
  //   The master provisions tenants; it does not run their calendars. It also
  //   cannot create either feed (the backend `forbid_master`s event writes), so
  //   the screen would be read-only noise in a panel that has no use for it.
  // 'calendar',
]);

/**
 * Grouped sidebar navigation (platform-master layout).
 * The master sees the super-admin panel minus MASTER_EXCLUDED_KEYS, plus a
 * master-only "Platform" section for onboarding tenants and provisioning super
 * admins — so it is NAV_SECTIONS with that section spliced in right after the
 * dashboard overview.
 */
export const NAV_SECTIONS_MASTER = [
  NAV_SECTIONS[0],
  {
    title: 'Platform',
    items: [
      {
        key: 'onboarding',
        label: 'Onboarding',
        path: '/dashboard/onboarding/new',
        icon: IconBuildingPlus,
        subtitle: 'Set up a new organization or center with its administrator',
      },
      {
        // Key/path unchanged (still the super-admin-backed list under the
        // hood — see views/master/SuperAdmins.jsx) — only the label, icon
        // and framing changed: the panel now reads organization-first
        // (one row per organization, its super admin alongside it) rather
        // than admin-first.
        key: 'super-admins',
        label: 'Organizations',
        path: '/dashboard/super-admins',
        icon: IconBuilding,
        subtitle: 'Every organization on the platform and its super administrator',
      },
    ],
  },
  // Same filter-then-drop-empty shape as NAV_SECTIONS_MENTOR_JURY below: strip
  // the excluded items, then drop any section they emptied so the sidebar never
  // renders a heading with nothing under it. The `...section` copy matters —
  // master must not share the section objects with NAV_SECTIONS, or a later
  // mutation of one would leak straight back into this panel.
  ...NAV_SECTIONS.slice(1)
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => !MASTER_EXCLUDED_KEYS.has(item.key)),
    }))
    .filter((section) => section.items.length > 0),
];

/** Grouped sidebar navigation (center-admin layout). */
export const NAV_SECTIONS_CENTER = [
  {
    title: 'Dashboard',
    items: [{ key: 'overview', label: 'Overview', path: '/dashboard', icon: IconLayoutGrid }],
  },
  {
    title: 'Programs',
    items: [
      {
        key: 'programs',
        label: 'Programs',
        path: '/dashboard/programs',
        icon: IconClipboardText,
        subtitle: 'Every program your centre is running — drafts, open windows, evaluated, closed.',
        // "Manage Collaborators" on a program's own page deep-links here; keep
        // the sidebar on "Programs" instead of jumping to "Collaborations".
        match: ['/dashboard/collaborations/manage/applications'],
      },
      {
        key: 'my-reviews',
        label: 'My Reviews',
        path: '/dashboard/my-reviews',
        icon: IconChecklist,
        subtitle: 'Applications awaiting your review.',
      },
      {
        key: 'evaluation-processes',
        label: 'Evaluation Processes',
        path: '/dashboard/evaluation-processes',
        icon: IconClipboardCheck,
        subtitle: 'Reusable evaluation workflows you can assign to any program.',
      },
      {
        key: 'evaluation-matrix',
        label: 'Evaluation Matrix',
        path: '/dashboard/evaluation-matrix',
        icon: IconScale,
        subtitle: 'Reusable scoring templates the jury evaluates applications against',
      },
    ],
  },
  {
    title: 'People',
    items: [
      {
        key: 'stakeholders',
        label: 'Stakeholders',
        path: '/dashboard/stakeholders',
        icon: IconUsers,
        subtitle: 'Mentors, jury members, and speakers in one view',
      },
    ],
  },
  {
    title: 'Grants',
    items: [
      {
        key: 'funds',
        label: 'Fund Catalog',
        pageTitle: 'Funds & schemes',
        path: '/dashboard/funds',
        icon: IconCash,
        subtitle: "Every fund your centre manages — sanctioned, disbursed, and what's left.",
      },
    ],
  },
  {
    title: 'Events',
    items: [
      {
        key: 'events',
        label: 'Events',
        path: '/dashboard/events',
        icon: IconCalendarEvent,
        subtitle: 'Workshops, demo days, and networking sessions across your center',
        // "Manage Collaborators" on an event's own page deep-links here; keep
        // the sidebar on "Events" instead of jumping to "Collaborations".
        match: ['/dashboard/collaborations/manage/events'],
      },
      {
        key: 'speakers',
        label: 'Speakers',
        path: '/dashboard/speakers',
        icon: IconMicrophone,
        subtitle: 'The speaker directory for your events and sessions',
      },
    ],
  },
  {
    title: 'Startups Directory',
    items: [
      { key: 'startups', label: 'Startups', path: '/dashboard/startups', icon: IconRocket, subtitle: 'All startups onboarded across every center.' },
      {
        key: 'mentoring',
        label: 'Mentoring Session',
        path: '/dashboard/mentoring',
        icon: IconSchool,
        subtitle: 'Scheduled mentoring sessions across your center',
      },
      {
        key: 'document-verification',
        label: 'Document Verification',
        path: '/dashboard/document-verification',
        icon: IconFileCheck,
        subtitle: 'Review and verify startup documents',
      },
    ],
  },
  {
    title: 'User Management',
    items: [
      {
        key: 'team',
        label: 'My Team',
        path: '/dashboard/roles',
        icon: IconShieldCheck,
        subtitle: 'Your center team, roles and permissions',
      },
    ],
  },
  {
    title: 'Reports',
    items: [{ key: 'reports', label: 'Reports', path: '/dashboard/reports', icon: IconReportAnalytics, subtitle: 'Analytics across applications, funding, mentoring and jury' }],
  },
];

// Collaboration lets a record owner (or a center admin) invite ANY user —
// including a Startup Founder, Jury member, or Mentor — to a specific
// Program/Event record. Defined here (ahead of the founder/jury/mentor panels
// below) so each of those layouts can carry its own "Workspace" section with
// this link, letting an invited founder/jury/mentor actually see and accept
// the invitation. Also reused further down for the center/org "Workspace"
// section (see TODOS_SECTION).
// Organizations, Invitations, and Sent all live as tabs inside
// CollaborationPanel now — one sidebar entry point for everything.
const COLLABORATIONS_ITEM = {
  key: 'collaborations',
  label: 'Collaborations',
  path: '/dashboard/collaborations',
  icon: IconUsers,
  subtitle: 'Records shared with you and your invitations',
  match: ['/dashboard/collaborations'],
};

/** Grouped sidebar navigation (startup-founder layout). */
export const NAV_SECTIONS_FOUNDER = [
  {
    title: 'Main',
    items: [{ key: 'overview', label: 'Overview', path: '/dashboard', icon: IconLayoutGrid }],
  },
  {
    title: 'Management',
    items: [
      {
        key: 'open-programs',
        label: 'Open Program',
        path: '/dashboard/open-programs',
        icon: IconClipboardText,
        subtitle: 'Programs open for applications',
        // Viewing a submitted application (e.g. from the dashboard's "My
        // Applications" widget) deep-links to the shared admin application route.
        match: ['/dashboard/programs'],
      },
      {
        key: 'my-mentors',
        label: 'My Mentors',
        path: '/dashboard/my-mentors',
        icon: IconSchool,
        subtitle: 'Mentors assigned to your startup',
      },
      {
        key: 'my-sessions',
        label: 'My Sessions',
        path: '/dashboard/my-sessions',
        icon: IconCalendarEvent,
        subtitle: 'Your scheduled mentoring sessions',
      },
      {
        // `my-events`, NOT `events`: this is the FOUNDER's own event list, and
        // it shared the centre item's key while using a different path. Since
        // NAV_ITEMS de-dupes by path, both survived — so any role holding
        // `events:read` whose panel lacked an events screen (a mentor, say) was
        // offered "Events" TWICE. A distinct key also lets
        // ROLE_PANEL_ONLY_ITEM_KEYS recognise it as a founder-panel screen,
        // which it could not while the key existed in the centre layout too.
        key: 'my-events',
        label: 'Events',
        path: '/dashboard/my-events',
        icon: IconCalendarEvent,
        subtitle: 'Workshops, demo days and networking sessions',
        // Event Details is the shared `/dashboard/events/:eventId` route (same
        // page every role's event list opens), not `/dashboard/my-events/:id` —
        // without this, resolveActiveSection finds no match for a founder on
        // that page and the header breadcrumb silently renders nothing.
        match: ['/dashboard/events'],
      },
      {
        key: 'my-funding',
        label: 'My Funding',
        path: '/dashboard/my-funding',
        icon: IconCash,
        subtitle: 'Funding awarded to your startup',
      },
    ],
  },
  {
    title: 'Reports',
    items: [{ key: 'reports', label: 'Reports', path: '/dashboard/reports', icon: IconReportAnalytics, subtitle: 'Track your applications, funding and mentoring' }],
  },
  {
    title: 'Profile',
    items: [
      {
        key: 'startup-profile',
        label: 'Startup Profile',
        path: '/dashboard/startup-profile',
        icon: IconRocket,
        subtitle: 'Manage your startup profile',
        // A founder's Documents permission is their OWN startup's documents —
        // the Documents section of this page — not the centre's Document
        // Verification module. Declaring it here keeps that module from being
        // granted into the founder's sidebar.
        covers: ['documents'],
      },
    ],
  },
  { title: 'Workspace', items: [COLLABORATIONS_ITEM] },
];

/** Grouped sidebar navigation (jury-member layout). */
export const NAV_SECTIONS_JURY = [
  {
    title: 'Main',
    items: [{ key: 'overview', label: 'Overview', path: '/dashboard', icon: IconLayoutGrid }],
  },
  {
    title: 'Programs',
    items: [
      {
        key: 'jury-programs',
        label: 'Programs',
        path: '/dashboard/jury-programs',
        icon: IconClipboardText,
        subtitle: 'Applications assigned to you',
        // Scoring an application deep-links to the shared admin application
        // route, and "View Startup" from there deep-links to /dashboard/startups/:id —
        // keep "Programs" highlighted through both.
        match: ['/dashboard/programs', '/dashboard/startups'],
        // A juror's `startups:read` is for that "View Startup" link, so it
        // must not add the centre's Startups directory to the sidebar.
        covers: ['startups'],
      },
    ],
  },
  { title: 'Workspace', items: [COLLABORATIONS_ITEM] },
];

/** Grouped sidebar navigation (mentor layout). */
export const NAV_SECTIONS_MENTOR = [
  {
    title: 'Main',
    items: [
      { key: 'overview', label: 'Overview', path: '/dashboard', icon: IconLayoutGrid },
      {
        key: 'mentor-calendar',
        label: 'Calendar',
        pageTitle: 'Session Scheduling',
        subtitle: 'Manage your mentoring sessions',
        path: '/dashboard/mentor-calendar',
        icon: IconCalendarEvent,
      },
    ],
  },
  {
    title: 'Management',
    items: [
      {
        key: 'mentor-sessions',
        label: 'Scheduled Sessions',
        pageTitle: 'My Sessions',
        path: '/dashboard/mentor-sessions',
        icon: IconClipboardText,
        subtitle: 'View and manage all your mentoring sessions',
        // Session planning detail lives under /dashboard/mentoring — keep this
        // item highlighted while a mentor is inside a session.
        match: ['/dashboard/mentoring'],
      },
      {
        key: 'assigned-startups',
        label: 'Assigned Startups',
        path: '/dashboard/assigned-startups',
        icon: IconRocket,
        subtitle: 'Startups you mentor',
        // Opening a startup's profile deep-links to /dashboard/startups/:id.
        match: ['/dashboard/startups'],
      },
    ],
  },
  { title: 'Workspace', items: [COLLABORATIONS_ITEM] },
];

/**
 * Merged sidebar for a user who holds BOTH mentor and jury assignments — every
 * mentor module PLUS the jury Programs module in a single panel, so they never
 * need to switch. Jury's own "Overview" section is dropped (mentor already has one).
 */
const mentorKeys = new Set(NAV_SECTIONS_MENTOR.flatMap((sec) => sec.items.map((it) => it.key)));
export const NAV_SECTIONS_MENTOR_JURY = [
  ...NAV_SECTIONS_MENTOR,
  // Drop the jury copy of any item the mentor layout already provides, so it
  // isn't listed twice in the merged panel. Nothing overlaps today (neither
  // panel carries "My Reviews" any more) — the guard stays for future items.
  ...NAV_SECTIONS_JURY.slice(1)
    .map((sec) => ({
      ...sec,
      items: sec.items.filter((it) => !mentorKeys.has(it.key)),
    }))
    .filter((sec) => sec.items.length > 0),
];

/**
 * Several panel layouts as one: sections merged by title, in first-seen order,
 * and an item dropped when an earlier layout already lists the same key or path
 * (every panel has its own Overview). The active role's layout goes first so
 * its sections lead. Copies — never mutates the shared layout objects.
 */
function mergePanelLayouts(layouts) {
  const sections = [];
  const keys = new Set();
  const paths = new Set();
  layouts.filter(Boolean).forEach((layout) => {
    layout.forEach((section) => {
      const items = section.items.filter((it) => !keys.has(it.key) && !paths.has(it.path));
      items.forEach((it) => { keys.add(it.key); paths.add(it.path); });
      if (!items.length) return;
      const target = sections.find((s) => s.title === section.title);
      if (target) target.items.push(...items);
      else sections.push({ ...section, items: [...items] });
    });
  });
  return sections;
}

const SUPER_ADMIN_ROLES = ['superadmin', 'super_admin', 'super admin', 'master'];
const FOUNDER_ROLES = ['startup_founder', 'startup founder', 'founder', 'startupfounder'];
// Mentor & Jury is one role with both panels — see getNavSections.
const JURY_ROLES = ['jury_member', 'jury member', 'jury', 'jurymember', 'jury panel', 'jury_panel', 'mentor_jury'];
const MENTOR_ROLES = ['mentor', 'mentor_jury'];

/**
 * Maps a sidebar item `key` to the RBAC permission resource that gates it
 * (the same resource keys the Role Editor's read/write matrix uses — see
 * `views/roles/data.js` RESOURCES). Items NOT listed here are never gated
 * (e.g. `overview`, and every founder/jury/mentor item), so those layouts are
 * unaffected. The Sidebar shows a gated item only when the signed-in user's
 * role has `read` on its resource — so a custom team member sees only the
 * modules their permissions allow. Only the MASTER bypasses this (`can()`
 * returns true for them); a super admin is gated like everyone else, so the
 * Super Admin role's matrix decides which modules appear in their sidebar.
 */
/**
 * To-dos is a shared workspace available to EVERY role (like Messages), so it is
 * spliced into each layout right after that layout's first (dashboard) section.
 * Mutating the exported section arrays here — before NAV_ITEMS is derived below —
 * keeps a single source of truth for the item instead of repeating it per role.
 */
const TODOS_ITEM = {
  key: 'todos',
  label: 'Workspace',
  path: '/dashboard/todos',
  icon: IconClipboardCheck,
  subtitle: 'Shared projects — assign work and track it in real time',
  match: ['/dashboard/todos/lists', '/dashboard/todos'],
};
const TODOS_SECTION = { title: 'Workspace', items: [TODOS_ITEM] };
// "To-dos" shows in the center layout (center admin and
// center team-user panels) AND the org super-admin layout — a super admin can
// use Workspace across the centers they oversee, scoped via the header's
// "Switch Center" control (see AssigneePicker/list_assignable on the backend
// for how assignment narrows without a center selected). NAV_SECTIONS_MASTER
// is derived from NAV_SECTIONS ABOVE this point, so master does not inherit
// it — every other panel (master / founder / jury / mentor) still hides both
// modules. Within the center layout both items are ALSO permission-gated on
// the `todos` resource (see NAV_RESOURCE below), so a custom center role sees
// them only once its role has `todos:read` ticked in the Role Editor.
[
  NAV_SECTIONS_CENTER,
  NAV_SECTIONS,
].forEach((sections) => {
  if (!sections.some((s) => s.items.some((i) => i.key === 'todos'))) {
    sections.splice(1, 0, TODOS_SECTION);
  }
});

// Collaboration is for center-facing staff (Center Admin / default org
// layouts, via TODOS_SECTION below) who invite guests — including a Startup
// Founder, Jury member, or Mentor (see COLLABORATIONS_ITEM above, already
// added to their own panels) — to a specific record. It has NO NAV_RESOURCE
// mapping, so the Sidebar's fail-open rule would otherwise show it to
// everyone — deliberately NOT added to the Master panel: a platform master
// isn't a per-record collaborator.
if (!TODOS_SECTION.items.some((i) => i.key === 'collaborations')) {
  TODOS_SECTION.items.push(COLLABORATIONS_ITEM);
}

export const NAV_RESOURCE = {
  // The dashboard is an ordinary module now: withhold `dashboard:read` and the
  // Overview item goes with it. WHICH dashboard renders is still decided by
  // role (see views/dashboard/DashboardHome) — permission decides whether the
  // page is offered at all, role decides which version of it you get.
  overview: 'dashboard',

  // --- Stakeholder panels -------------------------------------------------
  // The founder/mentor/jury pages are the SAME modules the centre panel shows,
  // seen from the other side of the relationship: a founder's "My Sessions" and
  // a centre's "Mentoring Sessions" are both `mentoring_sessions`. Mapping them
  // onto the same verbs is what lets one Role Editor row govern both, instead
  // of these panels being ungated as they were.
  //
  // The rows stay scoped by business rules regardless — a founder only ever
  // sees their own sessions, a juror only their assigned programmes. Permission
  // decides whether the page opens at all; the existing scoping decides what is
  // in it.
  'open-programs': 'applications',
  'jury-programs': 'applications',
  'my-mentors': 'mentors',
  'my-sessions': 'mentoring_sessions',
  'mentor-sessions': 'mentoring_sessions',
  'mentor-calendar': 'mentoring_sessions',
  'assigned-startups': 'startups',
  'startup-profile': 'startups',
  'my-funding': 'funding',
  programs: 'applications',
  // Both evaluation modules are governed by the `evaluations` verb pair, which
  // `applications:write` (Programs write) implies for a custom role. Sidebar
  // gates them explicitly (admin OR evaluations:write) rather than through this
  // read-only lookup — these entries keep the map honest about which resource
  // owns each module.
  'evaluation-processes': 'evaluations',
  'evaluation-matrix': 'evaluations',
  funds: 'funds',
  stakeholders: 'mentors',
  events: 'events',
  'my-events': 'events',
  // The common calendar draws BOTH feeds, but `events:read` is the narrower of
  // the two grants and the one the "Add Event" action needs, so it gates the
  // link. The sessions half is scoped independently by the backend on every
  // fetch, so a caller without mentoring reach simply sees no session blocks
  // rather than an error.
  calendar: 'events',
  speakers: 'speakers',
  startups: 'startups',
  // Its own resource, deliberately separate from `stakeholders`/`mentors`
  // above — a Center Team role sees this link only once granted
  // `mentoring_sessions:read` specifically in the Role Editor, whether or
  // not it also holds the Mentors (Stakeholders) grant. The two rows in the
  // Role Editor matrix are independent controls; this keeps the sidebar
  // agreeing with that instead of showing the link on Stakeholders access
  // alone.
  mentoring: 'mentoring_sessions',
  // Document Verification is the DOCUMENTS module. It was mapped onto
  // `startups` back when Documents had no row in the Role Editor, so Startups
  // was the only thing that could control it — that is no longer true, and the
  // mapping meant unticking Documents left the module sitting in the sidebar.
  'document-verification': 'documents',
  todos: 'todos',
  'todo-invitations': 'todos',
  // Collaborations lives inside the Workspace (TODOS_SECTION) and is gated
  // by the same `todos` resource — a role without Workspace:read should not
  // see it in the sidebar. Previously absent from this map, which caused the
  // fail-open path (`!resource → return true`) to always show it.
  collaborations: 'todos',
  team: 'users',
  roles: 'users',
  centers: 'centers',
  reports: 'reports',
};

/** Pick the sidebar sections for the signed-in user's role. */
// A staff role decides the LAYOUT; a panel held alongside it contributes its
// PERSONAL pages. The two are different things: a Center Admin who also sits on
// a jury doesn't need the jury panel's version of the product — they already
// administer all of it — but they do need the one page that is theirs alone,
// "applications assigned to you", which no admin screen answers for. Same for a
// mentor's own sessions and assigned startups, and a founder's own startup.
//
// Powers still come from the permission union (a jury role adds nothing a
// Center Admin lacks); this is only about reaching your own work.
const PANEL_PERSONAL_ITEM_KEYS = {
  founder: ['open-programs', 'my-mentors', 'my-sessions', 'my-events', 'startup-profile'],
  mentor: ['mentor-calendar', 'mentor-sessions', 'assigned-startups'],
  jury: ['jury-programs'],
};

/** The personal pages of every panel held, as one section — or null for none. */
function personalPanelSection(held) {
  const layouts = {
    founder: NAV_SECTIONS_FOUNDER,
    mentor: NAV_SECTIONS_MENTOR,
    jury: NAV_SECTIONS_JURY,
  };
  const items = [];
  const seen = new Set();
  ['founder', 'mentor', 'jury'].forEach((panel) => {
    if (!held.has(panel)) return;
    const wanted = PANEL_PERSONAL_ITEM_KEYS[panel];
    layouts[panel].forEach((section) => section.items.forEach((item) => {
      if (!wanted.includes(item.key) || seen.has(item.key)) return;
      seen.add(item.key);
      items.push(item);
    }));
  });
  return items.length ? { title: 'Assigned to Me', items } : null;
}

export const getNavSections = (role, hasCenter = false, assignments = []) => {
  const r = (role || '').toLowerCase();
  // A user holding BOTH mentor and jury assignments gets a single merged panel
  // (no switching) carrying every mentor and jury module together. Fold the
  // active role in too (jury_member → jury) so it triggers even when the
  // `assignments` list only carries the non-active panel.
  const held = new Set((assignments || []).map((a) => String(a).toLowerCase()));
  if (r === 'mentor_jury') {
    held.add('mentor');
    held.add('jury');
  } else if (r.includes('jury')) held.add('jury');
  else if (r === 'mentor') held.add('mentor');
  if (FOUNDER_ROLES.includes(r)) held.add('founder');
  // Founder + mentor and/or jury (multiple roles on one account): one merged
  // panel, same idea as mentor + jury below. Only when the active role IS a
  // panel role — a centre admin who also founded a startup keeps their centre
  // layout, exactly as before.
  const isPanelRole = FOUNDER_ROLES.includes(r) || JURY_ROLES.includes(r) || MENTOR_ROLES.includes(r);
  if (isPanelRole && held.has('founder') && (held.has('mentor') || held.has('jury'))) {
    return mergePanelLayouts([
      FOUNDER_ROLES.includes(r) ? NAV_SECTIONS_FOUNDER : null,
      held.has('mentor') && held.has('jury')
        ? NAV_SECTIONS_MENTOR_JURY
        : held.has('mentor') ? NAV_SECTIONS_MENTOR : NAV_SECTIONS_JURY,
      NAV_SECTIONS_FOUNDER,
    ]);
  }
  if (held.has('mentor') && held.has('jury')) return NAV_SECTIONS_MENTOR_JURY;
  // Master is a super-admin superset — check it first so it gets the extra
  // "Platform" section (the master isn't in SUPER_ADMIN_ROLES-only branch).
  if (r === 'master') return NAV_SECTIONS_MASTER;
  if (SUPER_ADMIN_ROLES.includes(r)) return NAV_SECTIONS;
  if (FOUNDER_ROLES.includes(r)) return NAV_SECTIONS_FOUNDER;
  if (JURY_ROLES.includes(r)) return NAV_SECTIONS_JURY;
  if (MENTOR_ROLES.includes(r)) return NAV_SECTIONS_MENTOR;
  // Everyone else pinned to a center — the center admin (whatever the role is
  // NAMED) and custom center-staff roles — gets the center layout, which the
  // Sidebar then filters down to the modules their role's permissions allow. No
  // center-admin role-name match is needed: the per-item permission gate + this
  // default keep the sidebar correct after a role rename.
  const staffLayout = hasCenter ? NAV_SECTIONS_CENTER : NAV_SECTIONS;
  // …plus their own panel pages, when they hold a panel too. Returns the shared
  // layout object untouched for the overwhelmingly common case of no panel.
  const personal = personalPanelSection(held);
  return personal ? [...staffLayout, personal] : staffLayout;
};

/**
 * Resolve the nav item that should be considered "active" for a pathname,
 * scoped to the signed-in role and aware of each item's optional `match`
 * sub-routes. Mirrors Sidebar's own highlighting so the header title/subtitle
 * never disagrees with which sidebar item is actually highlighted.
 */
/**
 * The base path an item matched, or null. Ties are broken by the MATCHED
 * base's length — not the item's canonical path length — so a short but
 * specific `match` entry beats an unrelated item whose own path happens to be
 * longer.
 */
export const matchedBaseFor = (item, pathname) => {
  if (item.path === '/dashboard') return pathname === '/dashboard' ? item.path : null;
  if (pathname === item.path || pathname.startsWith(`${item.path}/`)) return item.path;
  return (item.match || []).find((base) => pathname === base || pathname.startsWith(`${base}/`)) || null;
};

/**
 * Items to resolve a URL against: the role's own layout FIRST, then every item
 * the app has.
 *
 * A module can be shown to a role its layout never declared — the sidebar adds
 * modules the role has been granted (see Sidebar's granted-extras pass). Those
 * items are absent from `getNavSections(role)`, so resolving only against that
 * returned nothing: a mentor on /dashboard/stakeholders got no page title, no
 * breadcrumb and no sidebar highlight, while a centre admin on the same URL got
 * "Stakeholders / People".
 *
 * Falling back to the full list cannot widen anything: the route guard has
 * already decided whether this page may render at all, and this only chooses
 * the label and the highlight for a page that is already on screen. The role
 * layout still wins when it has a match, so role-specific naming ("My Events"
 * for a founder vs "Events") is unaffected.
 */
const resolutionItems = (role, hasCenter, assignments) => {
  const own = getNavSections(role, hasCenter, assignments).flatMap((s) => s.items);
  const ownPaths = new Set(own.map((i) => i.path));
  return [...own, ...NAV_ITEMS.filter((i) => !ownPaths.has(i.path))];
};

export const resolveActiveNavItem = (pathname, role, hasCenter = false, assignments = []) => {
  const items = resolutionItems(role, hasCenter, assignments);
  const candidates = items
    .map((item) => ({ item, base: matchedBaseFor(item, pathname) }))
    .filter((c) => c.base);
  // Stable: equal-length bases keep source order, so the role's own layout
  // (which comes first) still wins over the global fallback.
  candidates.sort((a, b) => b.base.length - a.base.length);
  return candidates[0]?.item;
};

/**
 * Resolve the sidebar SECTION title containing the active item for a path —
 * the first segment of the reference design's breadcrumb (`Workspace >
 * To-dos`, `Platform > Super Admins`, etc). Mirrors resolveActiveNavItem's
 * same longest-path-wins matching, just also keeping the section it came from.
 */
export const resolveActiveSection = (pathname, role, hasCenter = false, assignments = []) => {
  const sections = getNavSections(role, hasCenter, assignments);
  let best = null;
  sections.forEach((section) => {
    section.items.forEach((item) => {
      const base = matchedBaseFor(item, pathname);
      if (base && (!best || base.length > best.base.length)) {
        best = { section: section.title, item, base };
      }
    });
  });
  if (best) return best.section;
  // Not in this role's layout — a granted module. Name the section it lives in
  // canonically, the same place the sidebar files it under.
  const fallback = resolveActiveNavItem(pathname, role, hasCenter, assignments);
  return fallback ? canonicalSectionTitleFor(fallback.key) || null : null;
};

/**
 * Pages that stay in their panel even when the related permission is withheld.
 *
 * Some pages are the role's own workspace rather than a module they were
 * "granted": a founder without `startups:write` still has a startup profile to
 * look at. Removing it leaves the panel broken rather than restricted, so the
 * page stays and the READ/WRITE control is applied INSIDE it.
 *
 * They are still permission-aware — a page listed here renders read-only when
 * the write verb is missing; it simply never disappears.
 *
 * A mentor's Calendar is deliberately NOT here. It is the Sessions module: its
 * route is guarded on `mentoring_sessions:read`, so listing it without that
 * verb put a link in the sidebar that could only ever answer "no permission" —
 * and made it the mentor's landing page, so they met that toast on login.
 */
export const ALWAYS_VISIBLE_ITEM_KEYS = new Set(['startup-profile']);

/**
 * Which section a module belongs in, in the canonical (centre) layout.
 *
 * Used when a role has been GRANTED a module its own panel doesn't carry: the
 * item is placed in the section it would normally live in — Fund Catalog under
 * Grants, Events under Events — rather than collected into a separate bucket.
 * The sidebar is a list of modules the role can open; where a grant came from
 * is not something the person using it needs to see.
 */
export function canonicalSectionTitleFor(key) {
  const layouts = [NAV_SECTIONS_CENTER, NAV_SECTIONS_MASTER, NAV_SECTIONS];
  for (const layout of layouts) {
    for (const section of layout) {
      if (section.items?.some((item) => item.key === key)) return section.title;
    }
  }
  return undefined;
}

/**
 * Flattened, path-deduped list across BOTH layouts — used for routing.
 */
export const NAV_ITEMS = (() => {
  const all = [
    ...NAV_SECTIONS,
    ...NAV_SECTIONS_MASTER,
    ...NAV_SECTIONS_CENTER,
    ...NAV_SECTIONS_FOUNDER,
    ...NAV_SECTIONS_JURY,
    ...NAV_SECTIONS_MENTOR,
  ].flatMap((section) => section.items);
  const seen = new Set();
  return all.filter((item) => {
    if (seen.has(item.path)) return false;
    seen.add(item.path);
    return true;
  });
})();

/**
 * Screens that belong to ONE role's panel and are never granted into another's.
 *
 * A founder's "My Funding", a mentor's "Scheduled Sessions", a juror's
 * "Programs" are the SAME modules a centre sees, viewed from the other side of
 * the relationship — which is why NAV_RESOURCE maps them onto the same verbs,
 * so one Role Editor row governs both sides.
 *
 * That mapping is what the sidebar's "granted extras" pass keys on: a module
 * the role holds but its own panel doesn't list gets added. Correct for real
 * modules (tick Fund Catalog for a founder and they should see it); wrong for
 * these, because a Center Admin holding `mentoring_sessions:read` for the
 * centre's own Mentoring screen would be handed the MENTOR's personal inbox
 * as well. The pass used to exclude them by "has no resource mapping", which
 * stopped being true once they were mapped.
 *
 * Computed, not hand-listed: any item that appears only in a role panel's
 * layout and in none of the shared/centre/master ones.
 */
export const ROLE_PANEL_ONLY_ITEM_KEYS = (() => {
  const keysOf = (layouts) =>
    new Set(layouts.flatMap((sections) => sections.flatMap((s) => s.items.map((i) => i.key))));
  const shared = keysOf([NAV_SECTIONS, NAV_SECTIONS_CENTER, NAV_SECTIONS_MASTER]);
  const rolePanels = keysOf([
    NAV_SECTIONS_FOUNDER,
    NAV_SECTIONS_JURY,
    NAV_SECTIONS_MENTOR,
    NAV_SECTIONS_MENTOR_JURY,
  ]);
  return new Set([...rolePanels].filter((key) => !shared.has(key)));
})();

export default NAV_SECTIONS;
